import React, { useState, useEffect } from 'react';
import { X, Link as LinkIcon } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import { TrackImage } from '../../hooks/useEventManagement';

interface TicketShopUrlModalProps {
  showModal: boolean;
  onClose: () => void;
  selectedEvent: string | null;
  selectedTrackImage: TrackImage | null;
  ticketShopUrl: string;
  setTicketShopUrl: (url: string) => void;
  applyToAllDates: boolean;
  setApplyToAllDates: (apply: boolean) => void;
  fetchEvents: () => Promise<void>;
  events: any[];
}

const TicketShopUrlModal: React.FC<TicketShopUrlModalProps> = ({
  showModal,
  onClose,
  selectedEvent,
  selectedTrackImage,
  ticketShopUrl,
  setTicketShopUrl,
  applyToAllDates,
  setApplyToAllDates,
  fetchEvents,
  events
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedEventDate, setSelectedEventDate] = useState<string>('');
  const [currentEventId, setCurrentEventId] = useState<string | null>(selectedEvent);
  
  // When modal opens, find and select the closest upcoming date
  useEffect(() => {
    if (showModal && selectedTrackImage && selectedTrackImage.event_dates && selectedTrackImage.event_dates.length > 0) {
      // Get today's date without time
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Create a sorted array of dates with their event objects
      const datesWithEvents = selectedTrackImage.event_dates.map(dateObj => {
        // Parse date as local date
        const [year, month, day] = dateObj.event_date.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        
        // Find corresponding event
        const event = events.find(e => 
          e.track_image_id === selectedTrackImage.id && 
          e.event_date === dateObj.event_date
        );
        
        return {
          dateObj,
          date,
          event,
          // Calculate if date is in future and how far from today
          isFuture: date >= today,
          diffFromToday: Math.abs(date.getTime() - today.getTime())
        };
      });
      
      // First sort by future/past (future first)
      // Then sort future dates by closest first
      // Then sort past dates by most recent first
      datesWithEvents.sort((a, b) => {
        // First prioritize future dates
        if (a.isFuture && !b.isFuture) return -1;
        if (!a.isFuture && b.isFuture) return 1;
        
        // For dates that are both in future or both in past
        // Sort by closest to today
        return a.diffFromToday - b.diffFromToday;
      });
      
      // Select the first date after sorting
      if (datesWithEvents.length > 0) {
        const closestDate = datesWithEvents[0];
        setSelectedEventDate(closestDate.dateObj.event_date);
        
        // Set current event ID and ticket shop URL if event exists
        if (closestDate.event) {
          setCurrentEventId(closestDate.event.id);
          setTicketShopUrl(closestDate.event.ticket_shop_url || '');
        }
      }
    }
  }, [showModal, selectedTrackImage, events, setTicketShopUrl]);

  // When selectedEventDate changes, update the ticket shop URL and currentEventId
  useEffect(() => {
    if (selectedEventDate && selectedTrackImage) {
      // Find existing event
      const existingEvent = events.find(e => 
        e.track_image_id === selectedTrackImage.id && 
        e.event_date === selectedEventDate
      );
      
      if (existingEvent) {
        setCurrentEventId(existingEvent.id);
        setTicketShopUrl(existingEvent.ticket_shop_url || '');
      } else {
        setTicketShopUrl('');
        setCurrentEventId(null);
      }
    }
  }, [selectedEventDate, selectedTrackImage, events, setTicketShopUrl]);

  const handleSaveTicketShopUrl = async () => {
    if (!currentEventId) {
      toast.error('No event selected. Please select an event date first.');
      return;
    }
    
    setIsSubmitting(true);
    try {
      // Update the specific event with the ticket shop URL
      const { error } = await supabase
        .from('events')
        .update({ ticket_shop_url: ticketShopUrl || null })
        .eq('id', currentEventId);
        
      if (error) throw error;
      
      toast.success('Ticket shop URL updated successfully');
      onClose();
      fetchEvents();
    } catch (error) {
      console.error('Error updating ticket shop URL:', error);
      toast.error('Failed to update ticket shop URL');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-zinc-900 rounded-lg p-6 max-w-md w-full">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-white">
            Ticket Shop URL
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        {/* Event Date Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Event Date *
          </label>
          <select
            value={selectedEventDate || ''}
            onChange={(e) => setSelectedEventDate(e.target.value)}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white"
          >
            <option value="">Select event date</option>
            {selectedTrackImage?.event_dates?.map((eventDate) => (
              <option key={eventDate.id} value={eventDate.event_date}>
                {formatDateForDisplay(eventDate.event_date)}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-400 mt-1">
            Select the event date to update its ticket shop URL.
          </p>
        </div>
        
        <div className="mb-6 mt-6">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            External Ticket Shop URL
          </label>
          <input
            type="url"
            value={ticketShopUrl}
            onChange={(e) => setTicketShopUrl(e.target.value)}
            placeholder="https://ticketshop.example.com/event-123"
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
          />
          <p className="text-xs text-gray-400 mt-2">
            Enter the full URL where visitors can purchase tickets for this event. Leave empty if no external ticket shop is available.
          </p>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white py-2 rounded-md font-semibold"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSaveTicketShopUrl}
            className="flex-1 bg-primary-600 hover:bg-primary-700 text-white py-2 rounded-md font-semibold"
            disabled={isSubmitting || !selectedEventDate}
          >
            {isSubmitting ? 'Saving...' : 'Save URL'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Helper function to format date for display
const formatDateForDisplay = (dateString: string): string => {
  if (!dateString) return '';
  // Parse as local date to avoid timezone conversion
  const [year, month, day] = dateString.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export default TicketShopUrlModal;